import classNames from 'classnames/bind';
import React, { useCallback, useEffect } from 'react';
import styles from 'componets/two-factor-auth-dialog/index.module.scss';
import TextInput from 'componets/text-input';
import Button from 'componets/button';
import { InfoCircleFillIcon } from 'icons';
import {
  CHANGE_TOTP,
  FETCH_F2A_SECRET_STEP,
  SETUP_2FA_SUCCESSFUL_STEP,
  START_VALIDATE_2FA_AUTH,
  TOGGLE_2FA_DIALOG,
  TOGGLE_DISABLE_2FA_DIALOG,
  use2FaContext,
  VALIDATE_F2A_STEP,
} from 'features/profile/profile-account-setting-section/use-2fa';
import LoadingCard from 'componets/loading-card';
import { authenticatorUrl } from 'config/config';
import OuterLink from 'componets/outer-link';
import analytics from 'service/analytics';

const cx = classNames.bind(styles);

const TwoFactorAuthDialog = () => {
  const { state, dispatch, fetchTwoFactorAuth, validate2FaTotp } = use2FaContext();

  const toggle2FaDialog = useCallback(() => {
    document.body.style.overflow = 'inherit';
    dispatch({
      type: TOGGLE_2FA_DIALOG,
    });
  }, [dispatch]);

  const mask = cx({
    mask: true,
    show: state.show2FaDialog,
  });

  const step1 = cx({
    show: state.show2FaDialog,
    dialog: true,
    step1: true,
    approve: state.step !== FETCH_F2A_SECRET_STEP,
  });

  const step2 = cx({
    show: state.show2FaDialog,
    dialog: true,
    step2: true,
    active: state.step === VALIDATE_F2A_STEP,
    approve: state.step === SETUP_2FA_SUCCESSFUL_STEP,
  });

  const step3 = cx({
    show: state.show2FaDialog,
    dialog: true,
    step3: true,
    active: state.step === SETUP_2FA_SUCCESSFUL_STEP,
  });

  const onStep1 = useCallback(() => {
    dispatch({
      type: START_VALIDATE_2FA_AUTH,
    });
  }, [dispatch]);

  const changeTotp = useCallback(
    value => {
      dispatch({
        type: CHANGE_TOTP,
        payload: value,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (state.show2FaDialog) {
      fetchTwoFactorAuth();
    }
  }, [state.show2FaDialog]); //eslint-disable-line

  const onClose = () => {
    analytics.track('帳號資訊頁_帳號_兩步驟驗證_取消_click');
    toggle2FaDialog();
  };

  const validate2FaTotpClick = () => {
    analytics.track('帳號資訊頁_帳號_兩步驟驗證_驗證_click');
    validate2FaTotp();
  };

  return (
    <div
      className={mask}
      id="mask"
      onClick={e => {
        if (e.target.id === 'mask') {
          onClose();
        }
      }}
    >
      <div className={step1}>
        <div className={styles.dialogTitle}>設定兩步驟驗證</div>
        <div className={styles.dialogContent}>
          <ul>
            <li>
              <OuterLink
                link={authenticatorUrl(navigator.userAgent)}
                onClick={() => analytics.track('帳號資訊頁_帳號_兩步驟驗證_前往安裝_click')}
              >
                前往下載
              </OuterLink>
              安裝 Authenticator 應用程式。
            </li>
            <li>掃描以下 QR code 條碼。</li>
          </ul>
          <div className={styles.qrCode}>
            {state.base64Image ? (
              <img src={`data:image/png;base64, ${state.base64Image}`} width={150} height={150} alt="qrcode" />
            ) : (
              <LoadingCard />
            )}
          </div>
          <div className={styles.hint}>
            <InfoCircleFillIcon className={styles['info-icon']} width={20} height={20} />
            完成設定後 QR code
            條碼將不再顯示，每次設定將產生新的條碼，舊的條碼將自動失效。若遺失兩步驟驗證碼生成方式無法登入，請聯繫企業方案客服處理。
          </div>
        </div>
        <div className={styles.dialogButton}>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button
            onClick={() => {
              analytics.track('帳號資訊頁_帳號_兩步驟驗證_繼續_click');
              onStep1();
            }}
          >
            繼續
          </Button>
        </div>
      </div>
      <div className={step2}>
        <div className={styles.dialogTitle}>設定兩步驟驗證</div>
        <div className={styles.dialogContent}>
          請輸入應用程式中顯示的 6 位數驗證碼。
          <div className={styles['form-input']}>
            <TextInput
              type="text"
              digits="[0-9]"
              maxLength={6}
              value={state.totp}
              onKeyPressEnter={validate2FaTotpClick}
              errorMessage={state.error}
              placeholder="輸入驗證碼"
              onTextChange={changeTotp}
              onFocus={() => analytics.track('帳號資訊頁_帳號_兩步驟驗證_輸入_click')}
            />
          </div>
        </div>
        <div className={styles.dialogButton}>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button onClick={validate2FaTotpClick} loading={state.loading}>
            驗證
          </Button>
        </div>
      </div>
      <div className={step3}>
        <div className={styles.dialogTitle}>完成設定！</div>
        <div className={styles.dialogContent}>從現在起，登入時將需輸入顯示於 Authenticator 上的驗證碼。</div>
        <div className={styles.dialogButton}>
          <Button
            onClick={() => {
              analytics.track('帳號資訊頁_帳號_兩步驟驗證_完成_click');
              toggle2FaDialog();
            }}
          >
            完成
          </Button>
        </div>
      </div>
    </div>
  );
};

export const Disable2FaDialog = () => {
  const { state, dispatch, disable2FaAuth } = use2FaContext();

  const toggleDisable2FaDialog = useCallback(() => {
    dispatch({
      type: TOGGLE_DISABLE_2FA_DIALOG,
    });
  }, [dispatch]);

  const dialogClasses = cx({
    show: state.showDisable2FaDialog,
    dialog: true,
    step3: true,
    active: state.showDisable2FaDialog,
  });

  const mask = cx({
    mask: true,
    show: state.showDisable2FaDialog,
  });

  const onClose = () => {
    analytics.track('帳號資訊頁_帳號_兩步驟驗證_取消關閉_click');
    toggleDisable2FaDialog();
  };

  return (
    <div
      className={mask}
      id="mask"
      onClick={e => {
        if (e.target.id === 'mask') {
          onClose();
        }
      }}
    >
      <div className={dialogClasses}>
        <div className={styles.dialogTitle}>關閉兩步驟驗證</div>
        <div className={styles.dialogContent}>關閉設定後，登入只需帳號、密碼即可登入。</div>
        <div className={styles.dialogButton}>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button
            onClick={() => {
              analytics.track('帳號資訊頁_帳號_兩步驟驗證_確認關閉_click');
              disable2FaAuth();
            }}
            loading={state.loading}
          >
            確認
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthDialog;
